import React, { useState, useEffect } from 'react';
import Sidebar from '../components/SideBar';
import Header from '../components/Header';
import useGlobale from '../components/global';
import useMenuItems from '../components/Menu';
import {
  UtilisateurStats,
  UtilisateurFilters,
  UtilisateurList,
  UtilisateurDetail
} from '../components/utilisateur';
import api from '../components/api';
import Swal from 'sweetalert2';

const UtilisateursPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user } = useGlobale();
  const { menuItems } = useMenuItems();
  
  const [utilisateurs, setUtilisateurs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUtilisateur, setSelectedUtilisateur] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    ordering: '-date_joined'
  });

  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchStats();
    fetchUtilisateurs();
  }, [filters, currentPage]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/utilisateur/admin/utilisateurs/stats/');
      setStats(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des stats:', error);
    }
  };

  const fetchUtilisateurs = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', itemsPerPage);
      
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });

      const response = await api.get(`/api/utilisateur/admin/utilisateurs/?${params}`);
      console.log('Utilisateurs récupérés:', response.data);
      setUtilisateurs(response.data.results);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs:', error);
      setUtilisateurs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async (userId) => {
    try {
      const response = await api.get(`/api/utilisateur/admin/utilisateurs/${userId}/user-stats/`);
      setUserStats(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des stats utilisateur:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1); // Retour à la page 1 lors d'un changement de filtre
  };

  const handleViewUtilisateur = async (utilisateur) => {
    setSelectedUtilisateur(utilisateur);
    await fetchUserStats(utilisateur.id);
    setShowDetail(true);
  };

  const handleUpdateUtilisateur = async (id, data) => {
    try {
      await api.put(`/api/utilisateur/admin/utilisateurs/${id}/`, data);
      await Swal.fire({
        icon: 'success',
        title: 'Succès',
        text: 'Utilisateur mis à jour avec succès',
        confirmButtonColor: '#FF6B00',
      });
      fetchUtilisateurs();
      fetchStats();
      setShowDetail(false);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Impossible de mettre à jour l\'utilisateur',
        confirmButtonColor: '#FF6B00',
      });
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const response = await api.post(`/api/utilisateur/admin/utilisateurs/${id}/toggle-status/`);
      await Swal.fire({
        icon: 'success',
        title: 'Succès',
        text: response.data.message,
        confirmButtonColor: '#FF6B00',
      });
      fetchUtilisateurs();
      fetchStats();
      setShowDetail(false);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Impossible de changer le statut',
        confirmButtonColor: '#FF6B00',
      });
    }
  };

  const handleChangePassword = async (utilisateur) => {
    const { value: password } = await Swal.fire({
      title: `Changer le mot de passe de ${utilisateur.username}`,
      input: 'password',
      inputLabel: 'Nouveau mot de passe',
      inputPlaceholder: 'Entrez le nouveau mot de passe',
      inputAttributes: {
        minlength: 6,
        autocapitalize: 'off',
        autocorrect: 'off'
      },
      showCancelButton: true,
      confirmButtonText: 'Changer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#FF6B00',
      cancelButtonColor: '#6B7280',
      inputValidator: (value) => {
        if (!value) {
          return 'Le mot de passe est requis';
        }
        if (value.length < 6) {
          return 'Le mot de passe doit contenir au moins 6 caractères';
        }
      }
    });

    if (password) {
      try {
        await api.post(`/api/utilisateur/admin/utilisateurs/${utilisateur.id}/change-password/`, {
          new_password: password
        });
        await Swal.fire({
          icon: 'success',
          title: 'Succès',
          text: 'Mot de passe modifié avec succès',
          confirmButtonColor: '#FF6B00',
        });
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: error.response?.data?.error || 'Impossible de changer le mot de passe',
          confirmButtonColor: '#FF6B00',
        });
      }
    }
  };

  const handleDeleteUtilisateur = async (id) => {
    const result = await Swal.fire({
      title: 'Confirmer la suppression',
      text: 'Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#FF6B00',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/api/utilisateur/admin/utilisateurs/${id}/`);
        await Swal.fire({
          icon: 'success',
          title: 'Supprimé',
          text: 'Utilisateur supprimé avec succès',
          confirmButtonColor: '#FF6B00',
        });
        fetchUtilisateurs();
        fetchStats();
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
        text: 'Impossible de supprimer l\'utilisateur',
        confirmButtonColor: '#FF6B00',
        });
      }
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="flex min-h-screen bg-[#F4F8E9]">
      <Sidebar
        isOpen={isSidebarOpen}
        menuItems={menuItems}
        onClose={() => setIsSidebarOpen(false)}
      />

      <div className="flex-1 flex flex-col w-full">
        <Header
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          user={user}
        />

        <main className="flex-1 w-full p-6">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">
              Gestion des Utilisateurs
            </h1>
            <p className="text-gray-600 mt-2">
              Gérez tous les utilisateurs de la plateforme FONAP
            </p>
          </div>

          <div className="space-y-6">
            {/* Statistiques */}
            {stats && <UtilisateurStats stats={stats} />}

            {/* Filtres */}
            <UtilisateurFilters
              filters={filters}
              onFilterChange={handleFilterChange}
            />

            {/* Liste des utilisateurs */}
            <UtilisateurList
              utilisateurs={utilisateurs}
              onView={handleViewUtilisateur}
              onChangePassword={handleChangePassword}
              onDelete={handleDeleteUtilisateur}
              loading={loading}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>

          {/* Modal de détails */}
          {showDetail && selectedUtilisateur && (
            <UtilisateurDetail
              utilisateur={selectedUtilisateur}
              userStats={userStats}
              onClose={() => setShowDetail(false)}
              onUpdate={handleUpdateUtilisateur}
              onToggleStatus={handleToggleStatus}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default UtilisateursPage;
